import { z } from "zod";

export const agentCapabilitiesSchema = z.object({
  create_sessions: z.boolean(),
  research_campaign: z.boolean().optional(),
  approve_verified_leads: z.boolean(),
  approve_outbound_actions: z.boolean(),
});

export const availableAgentSchema = z.object({
  key: z.string().min(1),
  display_name: z.string().min(1),
  description: z.string().min(1).optional(),
  team_size: z.number().int().positive().optional(),
  status: z.enum(["available", "unavailable"]),
  capabilities: agentCapabilitiesSchema,
});

export const availableAgentsSchema = z.array(availableAgentSchema);

export const sessionActorSchema = z.object({
  id: z.number().int().nullable(),
  name: z.string().nullable(),
});

const normalizedEventBaseSchema = z.object({
  id: z.string().min(1),
  author: z.string().min(1),
  created_at: z.string().datetime({ offset: true }).nullable(),
});

const messageEventSchema = normalizedEventBaseSchema.extend({
  type: z.literal("message"),
  content: z.object({
    text: z.string(),
  }),
});

const agentStatusEventSchema = normalizedEventBaseSchema.extend({
  type: z.literal("agent_status"),
  content: z.object({
    status: z.string(),
    label: z.string(),
  }),
});

const toolEventContentSchema = z.object({
  tool: z.string(),
  label: z.string(),
  status: z.string(),
});

export const approvalDecisionSchema = z.enum([
  "approved",
  "rejected",
  "revision_requested",
  "confirmed_sent",
  "confirmed_not_sent",
]);

export const approvalContinuationStatusSchema = z.enum([
  "pending",
  "completed",
  "failed",
]);

export const verifiedTargetSchema = z.object({
  target_id: z.string().min(1),
  lead_label: z.string().min(1),
  channel: z.enum(["email", "whatsapp", "phone", "linkedin"]),
  value: z.string().min(1),
  lead_id: z.string().min(1).nullable().optional(),
  person_id: z.string().min(1).nullable().optional(),
  person_name: z.string().min(1).nullable().optional(),
  role_title: z.string().min(1).nullable().optional(),
  segment: z.string().min(1).nullable().optional(),
  persona_code: z.string().min(1).nullable().optional(),
  persona_classification: z.string().min(1).nullable().optional(),
  qualification_score: z.number().int().min(0).max(100).nullable().optional(),
  rationale: z.array(z.string()).optional(),
  evidence_urls: z.array(z.string()).optional(),
  red_flags: z.array(z.string()).optional(),
});

const approvalProposalBaseSchema = z.object({
  approval_id: z.string().min(1),
  status: z.literal("pending"),
  title: z.string().min(1),
});

export const verifiedLeadApprovalProposalSchema =
  approvalProposalBaseSchema.extend({
    approval_type: z.literal("verified_leads"),
    targets: z.array(verifiedTargetSchema).min(1),
  });

export const outboundActionApprovalProposalSchema =
  approvalProposalBaseSchema.extend({
    approval_type: z.literal("outbound_action"),
    action_id: z.string().min(1),
    action_fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    channel: z.enum([
      "email",
      "whatsapp",
      "phone",
      "linkedin",
      "linkedin_connection",
    ]),
    recipient: z.string().min(1),
    subject: z.string().nullable(),
    content_preview: z.string().min(1),
  });

export const outboundReconciliationApprovalProposalSchema =
  approvalProposalBaseSchema.extend({
    approval_type: z.literal("outbound_reconciliation"),
    action_id: z.string().min(1),
    action_fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    channel: z.enum(["email", "whatsapp", "linkedin", "linkedin_connection"]),
    recipient: z.string().min(1),
    subject: z.string().nullable(),
    content_preview: z.string().min(1),
    dispatch_status: z.enum(["dispatching", "delivery_unknown"]),
    dispatch_started_at: z.string().datetime({ offset: true }),
    reconciliation_available_at: z.string().datetime({ offset: true }),
    provider_reference: z.string().nullable(),
  });

export const approvalProposalSchema = z.discriminatedUnion("approval_type", [
  verifiedLeadApprovalProposalSchema,
  outboundActionApprovalProposalSchema,
  outboundReconciliationApprovalProposalSchema,
]);

export const approvalDecisionPayloadSchema = z
  .object({
    approval_id: z.string().min(1),
    decision: approvalDecisionSchema,
    selected_target_ids: z.array(z.string().min(1)).default([]),
    revision_notes: z.string().trim().max(2_000).nullable().default(null),
  })
  .strict()
  .superRefine((payload, context) => {
    if (payload.decision === "revision_requested" && !payload.revision_notes) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["revision_notes"],
        message: "Explain what should be changed.",
      });
    }
  });

export const structuredApprovalRecordedContentSchema = z.object({
  approval_id: z.string().min(1),
  approval_type: z.enum([
    "verified_leads",
    "outbound_action",
    "outbound_reconciliation",
  ]),
  decision: approvalDecisionSchema,
  status: approvalDecisionSchema,
  label: z.string().min(1),
  selected_target_ids: z.array(z.string()),
  revision_notes: z.string().nullable(),
  continuation_status: approvalContinuationStatusSchema,
  decided_by: sessionActorSchema,
  decided_at: z.string().datetime({ offset: true }),
});

const toolStartedEventSchema = normalizedEventBaseSchema.extend({
  type: z.literal("tool_started"),
  content: toolEventContentSchema,
});

const toolCompletedEventSchema = normalizedEventBaseSchema.extend({
  type: z.literal("tool_completed"),
  content: toolEventContentSchema,
});

const approvalRequiredEventSchema = normalizedEventBaseSchema.extend({
  type: z.literal("approval_required"),
  content: z.union([
    approvalProposalSchema,
    z.object({
      label: z.string().optional(),
      status: z.string().optional(),
    }),
  ]),
});

const approvalRecordedEventSchema = normalizedEventBaseSchema.extend({
  type: z.literal("approval_recorded"),
  content: z.union([
    structuredApprovalRecordedContentSchema,
    toolEventContentSchema,
  ]),
});

export const companyAssociationReviewPayloadSchema = z
  .object({
    kind: z.literal("company_association_review"),
    version: z.literal(1),
    review_id: z.string().min(1),
    person: z
      .object({
        person_id: z.string().min(1),
        name: z.string().min(1).nullable(),
        role: z.string().min(1).nullable(),
        linkedin_url: z
          .string()
          .url()
          .regex(/^https:\/\/(?:[a-z0-9-]+\.)*linkedin\.com\/in\//i),
      })
      .strict(),
    target_company: z
      .object({
        lead_id: z.string().min(1).nullable(),
        name: z.string().min(1),
        location: z.string().min(1).nullable(),
        place_id: z.string().min(1).nullable(),
      })
      .strict(),
    observed_employment: z
      .object({
        company_name: z.string().min(1).nullable(),
        match_basis: z.string().min(1).nullable(),
        checked_at: z.string().datetime({ offset: true }).nullable(),
      })
      .strict(),
    reason_code: z.string().min(1),
  })
  .strict();

const requestInputRequiredContentSchema = z.object({
  interrupt_id: z.string().min(1),
  message: z.string().min(1),
  payload: z.unknown().nullable(),
  response_schema: z.record(z.unknown()).nullable(),
  status: z.literal("pending"),
});

const inputRequiredEventSchema = normalizedEventBaseSchema.extend({
  type: z.literal("input_required"),
  content: requestInputRequiredContentSchema,
});

const inputRecordedEventSchema = normalizedEventBaseSchema.extend({
  type: z.literal("input_recorded"),
  content: z.object({
    interrupt_id: z.string().min(1),
    response: z.unknown(),
    status: z.literal("answered"),
  }),
});

const artifactEventSchema = normalizedEventBaseSchema.extend({
  type: z.literal("artifact"),
  content: z.object({
    mime_type: z.string(),
    label: z.string(),
  }),
});

const errorEventSchema = normalizedEventBaseSchema.extend({
  type: z.literal("error"),
  content: z.object({
    message: z.string(),
  }),
});

export const normalizedEventSchema = z.discriminatedUnion("type", [
  messageEventSchema,
  agentStatusEventSchema,
  toolStartedEventSchema,
  toolCompletedEventSchema,
  approvalRequiredEventSchema,
  approvalRecordedEventSchema,
  inputRequiredEventSchema,
  inputRecordedEventSchema,
  artifactEventSchema,
  errorEventSchema,
]);

export const agentRunStatusSchema = z.enum([
  "queued",
  "running",
  "cancel_requested",
  "completed",
  "failed",
  "cancelled",
  "interrupted",
]);

export const agentRunSchema = z.object({
  id: z.string().uuid(),
  session_id: z.string().min(1),
  operation: z.enum(["message", "workflow_start"]),
  status: agentRunStatusSchema,
  correlation_id: z.string().min(1),
  created_at: z.string().datetime({ offset: true }),
  started_at: z.string().datetime({ offset: true }).nullable(),
  finished_at: z.string().datetime({ offset: true }).nullable(),
  error: z
    .object({
      code: z.string().min(1),
      message: z.string(),
    })
    .nullable(),
});

const researchTargetContactSchema = z.object({
  channel: z.enum(["email", "whatsapp", "phone", "linkedin"]),
  value: z.string().min(1),
  display_status: z.enum(["found", "verifying", "verified"]),
});

const researchTargetDeliverySchema = z.object({
  status: z.enum(["sending", "sent"]),
  channel: z.enum(["email", "whatsapp", "phone", "linkedin"]),
  sent_at: z.string().datetime({ offset: true }).nullable(),
});

export const researchTargetConnectionStatusSchema = z.enum([
  "checking",
  "connection_check_failed",
  "connection_request_needed",
  "pending_connection_approval",
  "approved_to_connect",
  "connection_request_sending",
  "awaiting_acceptance",
  "connected",
  "connection_request_rejected",
  "connection_request_failed",
  "connection_delivery_unknown",
  "connection_acceptance_expired",
]);

export const researchTargetResearchStatusSchema = z.enum([
  "research_pending",
  "in_progress",
  "needs_review",
  "verified",
  "rejected",
  "discovered",
  "verifying_profile",
  "company_verified",
  "company_ambiguous",
  "company_mismatch",
  "persona_ineligible",
  "qualified",
  "verification_failed",
]);

const researchTargetLeadSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  role_title: z.string().min(1).nullable(),
  approval_status: z.enum(["pending_review", "approved"]),
  research_status: researchTargetResearchStatusSchema.nullable().optional(),
  connection_status: researchTargetConnectionStatusSchema.nullable().optional(),
  contacts: z.array(researchTargetContactSchema),
  delivery: researchTargetDeliverySchema.nullable(),
  awaiting_reply: z.boolean(),
});

const researchTargetCompanySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  qualification_status: z.enum([
    "shortlisted",
    "researching",
    "qualified",
    "rejected",
    "skipped",
  ]),
  rejection_reasons: z.array(z.string().min(1)),
  leads: z.array(researchTargetLeadSchema),
});

export const researchTargetsSchema = z.object({
  status: z.enum(["searching", "review", "sending", "sent"]),
  activity_label: z.string().min(1).nullable(),
  companies: z.array(researchTargetCompanySchema),
});

export const researchTargetIndexCompanyStatusSchema = z.enum([
  "research_pending",
  "in_progress",
  "needs_review",
  "researched",
  "rejected",
  "skipped",
]);

export const researchTargetIndexLeadStatusSchema = z.enum([
  "research_pending",
  "in_progress",
  "needs_review",
  "verified",
  "invite_sent",
  "rejected",
]);

const researchTargetIndexContactSchema = z.object({
  channel: z.enum(["email", "whatsapp", "phone", "linkedin"]),
  value: z.string().min(1),
  display_status: z.enum(["found", "verifying", "verified"]),
});

const researchTargetIndexLeadSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  role_title: z.string().nullable(),
  status: researchTargetIndexLeadStatusSchema,
  approval_status: z.enum(["pending_review", "approved"]).optional(),
  connection_status: researchTargetConnectionStatusSchema.nullable().optional(),
  research_status: researchTargetResearchStatusSchema.nullable().optional(),
  updated_at: z.string().datetime({ offset: true }).nullable(),
  contacts: z.array(researchTargetIndexContactSchema),
});

const researchTargetIndexRowSchema = z.object({
  id: z.string().min(1),
  source_id: z.string().min(1),
  session_id: z.string().min(1),
  session_title: z.string().min(1),
  place_id: z.string().nullable().optional(),
  name: z.string().min(1),
  domain: z.string().nullable(),
  segment: z.string().nullable(),
  status: researchTargetIndexCompanyStatusSchema,
  updated_at: z.string().datetime({ offset: true }).nullable(),
  rejection_reasons: z.array(z.string()),
  leads: z.array(researchTargetIndexLeadSchema),
});

export const researchTargetIndexSchema = z.object({
  summary: z.object({
    companies: z.number().int().nonnegative(),
    in_progress: z.number().int().nonnegative(),
    research_pending: z.number().int().nonnegative(),
    individuals_found: z.number().int().nonnegative(),
  }),
  sessions: z.array(
    z.object({ id: z.string().min(1), title: z.string().min(1) }),
  ),
  results: z.array(researchTargetIndexRowSchema),
  pagination: z.object({
    count: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    page_size: z.number().int().positive(),
    next_page: z.number().int().positive().nullable(),
  }),
});

export const createAgentRunPayloadSchema = z.discriminatedUnion("operation", [
  z.object({
    operation: z.literal("message"),
    text: z.string().trim().min(1).max(10_000),
  }),
  z.object({
    operation: z.literal("workflow_start"),
  }),
]);

export const agentSessionSchema = z.object({
  id: z.string().min(1),
  agent_key: z.string().min(1),
  title: z.string(),
  status: z.string().min(1),
  created_by: sessionActorSchema,
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  events: z.array(normalizedEventSchema).optional().default([]),
  current_run: agentRunSchema.nullable().optional(),
  research_targets: researchTargetsSchema.nullable().optional(),
});

export const agentSessionsSchema = z.array(agentSessionSchema);

export const messageResponseSchema = z.object({
  session_id: z.string().min(1),
  events: z.array(normalizedEventSchema),
});

export const requestInputResponsePayloadSchema = z
  .object({
    response: z.unknown(),
  })
  .strict();

export const messageDeltaStreamDataSchema = z.object({
  session_id: z.string().min(1),
  message_key: z.string().min(1),
  delta: z.string(),
});

export const agentEventStreamDataSchema = z.object({
  session_id: z.string().min(1),
  message_key: z.string().min(1).optional(),
  event: normalizedEventSchema,
});

export const completeStreamDataSchema = z.object({
  session_id: z.string().min(1),
  correlation_id: z.string().min(1),
});

export const errorStreamDataSchema = z.object({
  session_id: z.string().min(1),
  type: z.literal("error"),
  code: z.string().min(1),
  message: z.string().min(1),
  correlation_id: z.string().min(1),
});

export const approvalRecordSchema = z.object({
  approval_id: z.string().min(1),
  approval_type: z.enum([
    "verified_leads",
    "outbound_action",
    "outbound_reconciliation",
  ]),
  decision: approvalDecisionSchema,
  status: z.enum([
    "approved",
    "rejected",
    "revision_requested",
    "confirmed_sent",
    "confirmed_not_sent",
    "stale",
  ]),
  selected_target_ids: z.array(z.string()),
  revision_notes: z.string().nullable(),
  continuation_status: approvalContinuationStatusSchema,
  decided_by: sessionActorSchema,
  decided_at: z.string().datetime({ offset: true }),
  events: z.array(normalizedEventSchema),
});

export const createAgentSessionPayloadSchema = z.object({
  session_name: z.string().trim().min(1).max(255).optional(),
  initial_context: z
    .object({
      campaign_id: z.string().min(1).optional(),
      campaign: z
        .object({
          target_segments: z
            .array(
              z.enum([
                "P1_HOSPITALITY",
                "P2_FOOD_SERVICE",
                "P3_PRIVATE_LABEL_INSTITUTIONAL",
                "P4_FASHION_DESIGNER_BRANDS",
                "P5_REAL_ESTATE_COMPANIES",
                "P6_VEHICLE_SHOWROOMS",
                "FOOD_DISTRIBUTOR_WHOLESALER",
                "HORECA_SUPPLIER",
                "FRESH_FROZEN_IMPORTER",
                "MULTIBRANCH_GROCERY_CASH_CARRY",
              ]),
            )
            .min(1)
            .max(6),
          requested_company_count: z.number().int().min(1).max(100),
          discovery_mode: z.enum(["new", "refresh"]).optional(),
        })
        .optional(),
    })
    .optional(),
});

export type AgentCapabilities = z.infer<typeof agentCapabilitiesSchema>;
export type AvailableAgent = z.infer<typeof availableAgentSchema>;
export type NormalizedEvent = z.infer<typeof normalizedEventSchema>;
export type AgentSession = z.infer<typeof agentSessionSchema>;
export type ResearchTargets = z.infer<typeof researchTargetsSchema>;
export type ResearchTargetIndex = z.infer<typeof researchTargetIndexSchema>;
export type ResearchTargetIndexRow = z.infer<
  typeof researchTargetIndexRowSchema
>;
export type ResearchTargetIndexCompanyStatus = z.infer<
  typeof researchTargetIndexCompanyStatusSchema
>;
export type ResearchTargetIndexLeadStatus = z.infer<
  typeof researchTargetIndexLeadStatusSchema
>;
export type ResearchTargetConnectionStatus = z.infer<
  typeof researchTargetConnectionStatusSchema
>;
export type ResearchTargetResearchStatus = z.infer<
  typeof researchTargetResearchStatusSchema
>;
export type CompanyAssociationReviewPayload = z.infer<
  typeof companyAssociationReviewPayloadSchema
>;
export type AgentRun = z.infer<typeof agentRunSchema>;
export type AgentRunStatus = z.infer<typeof agentRunStatusSchema>;
export type CreateAgentRunPayload = z.infer<typeof createAgentRunPayloadSchema>;
export type MessageResponse = z.infer<typeof messageResponseSchema>;
export type AgentStreamFrame =
  | {
      event: "message_delta";
      id?: string;
      data: z.infer<typeof messageDeltaStreamDataSchema>;
    }
  | {
      event: "agent_event";
      id?: string;
      data: z.infer<typeof agentEventStreamDataSchema>;
    }
  | {
      event: "complete";
      id?: string;
      data: z.infer<typeof completeStreamDataSchema>;
    }
  | {
      event: "error";
      id?: string;
      data: z.infer<typeof errorStreamDataSchema>;
    };
export type CreateAgentSessionPayload = z.infer<
  typeof createAgentSessionPayloadSchema
>;
export type ApprovalProposal = z.infer<typeof approvalProposalSchema>;
export type RequestInputRequiredContent = z.infer<
  typeof requestInputRequiredContentSchema
>;
export type RequestInputResponsePayload = z.infer<
  typeof requestInputResponsePayloadSchema
>;
export type VerifiedLeadApprovalProposal = z.infer<
  typeof verifiedLeadApprovalProposalSchema
>;
export type OutboundActionApprovalProposal = z.infer<
  typeof outboundActionApprovalProposalSchema
>;
export type OutboundReconciliationApprovalProposal = z.infer<
  typeof outboundReconciliationApprovalProposalSchema
>;
export type ApprovalDecisionPayload = z.infer<
  typeof approvalDecisionPayloadSchema
>;
export type ApprovalRecord = z.infer<typeof approvalRecordSchema>;
export type StructuredApprovalRecordedContent = z.infer<
  typeof structuredApprovalRecordedContentSchema
>;

export function isAvailableAgent(agent: AvailableAgent) {
  return agent.status === "available";
}
